import { lazy, Suspense, useLayoutEffect, useRef, useState } from 'react';

import { cn } from './cn';
import type { CodeSlotsProps, CodeSlotsStatus } from './code-slots';

// O CodeSlots e a `motion` chegam sob demanda (chunk `code-slots-*.js`, teto no .size-limit.json).
const CodeSlots = lazy(() => import('./code-slots'));

export type CodeInputStatus = CodeSlotsStatus;

const MIN_SLOT = 40;
const MAX_SLOT = 52;

/** Slots de 40 a 52 px que ocupam a largura toda do contêiner (alinhados ao botão); a sobra vai para o vão. */
export function slotMetrics(width: number, length: number): { readonly slotSize: number; readonly gap: number } {
  const baseGap = width <= 340 ? 6 : 8;
  const fit = Math.floor((width - baseGap * (length - 1)) / length);
  const slotSize = Math.min(MAX_SLOT, Math.max(MIN_SLOT, fit));
  if (length < 2) return { slotSize, gap: baseGap };
  // Abaixo de ~280 px nem o slot mínimo cabe: vão mínimo, sem sobrepor.
  const gap = Math.max(4, (width - slotSize * length) / (length - 1));
  return { slotSize, gap };
}

const coarse = () => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

export type CodeInputProps = Omit<CodeSlotsProps, 'slotSize' | 'gap'>;

/**
 * Código de verificação (MFA, step-up) em slots, na identidade do Lume. Mede a largura e ajusta o tamanho
 * dos slots; no toque, rola o campo para o centro ao focar, para o teclado não cobrir (sheet do iPhone).
 * Enquanto o chunk chega, desenha os slots vazios no mesmo tamanho (sem pulo de layout).
 */
export function CodeInput({ length = 6, className, onFocus, slotColor, ...props }: CodeInputProps) {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(360);
  useLayoutEffect(() => {
    const element = box.current;
    if (!element) return undefined;
    const measure = () => setWidth(element.clientWidth || 360);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const { slotSize, gap } = slotMetrics(width, length);
  const height = Math.max(44, Math.round(slotSize * 1.18));

  const focus = () => {
    onFocus?.();
    if (!coarse()) return;
    // Depois de o teclado subir (o iOS anima ~250 ms), o campo vai para o centro da área visível.
    setTimeout(() => box.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300);
  };

  return (
    <div ref={box} data-slot="code-input-box" className={cn('w-full min-w-0', className)}>
      <Suspense
        fallback={
          <div aria-hidden className="inline-flex" style={{ gap }}>
            {Array.from({ length }, (_, index) => (
              <span
                key={index}
                className="rounded-control border border-input"
                style={{ width: slotSize, height, background: slotColor ?? 'var(--lume-bg-inset)' }}
              />
            ))}
          </div>
        }
      >
        <CodeSlots {...props} length={length} slotSize={slotSize} gap={gap} onFocus={focus} {...(slotColor ? { slotColor } : {})} />
      </Suspense>
    </div>
  );
}
